import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import billingService from '../../services/billingService';
import { toast } from 'react-toastify';
import html2pdf from 'html2pdf.js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faUndo, faSearch, faPrint, faFileExport, faEnvelope, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import Heading from '../../components/Common/Heading/Heading';
import SearchBar from '../../components/Common/SearchBar/SearchBar';
import ListScreenPaginationBar from '../../components/Common/ListScreenPaginationBar/ListScreenPaginationBar';
import Table from '../../components/Common/Table/Table';
import StatusBadge from '../../components/Common/StatusBadge/StatusBadge';
import '../../components/Common/ListScreenPageLayout/listScreenPage.css';
import './ManageBillingReport.css';
import { getPracticeBillingPeriod } from '../../utils/billingPeriodUtils';
import { readBillingPracticeCache, writeBillingPracticeCache } from '../../utils/billingPracticeCache';

const getStatusOrder = (status) => {
  if (status == null || status === '') return 99;
  const s = String(status).toUpperCase();
  if (s === '2' || s === 'ACTIVE' || s === 'A') return 1;
  if (s === '3' || s === 'PENDING' || s === 'P') return 2;
  if (s === '4' || s === 'LOCKED' || s === 'L' || s === 'LOST') return 3;
  return 99;
};

const normalizeBillingStatus = (status) => {
  if (status == null || status === '') return '';
  const s = String(status);
  if (/^\d+$/.test(s)) {
    const map = { 2: 'Active', 3: 'Pending', 4: 'Lost' };
    return map[s] || s;
  }
  if (s.toUpperCase() === 'LOCKED') return 'Lost';
  return s;
};

const formatPersonName = (last, first) => {
  const name = `${last || ''}, ${first || ''}`.replace(/^,\s*|\s*,$/g, '').trim();
  return name || '';
};

const mapBillingPatientRow = (patient) => {
  const patientName = formatPersonName(patient.last_name, patient.first_name) || 'N/A';
  const dob = patient.date_of_birth || '';
  const rpmStartDate = patient.rpm_start_date || '';
  const rpmMs = rpmStartDate ? new Date(rpmStartDate).getTime() : 0;
  const uploadRaw = patient.upload_date_time || patient.upload_date || null;
  const uploadMs = uploadRaw ? new Date(uploadRaw).getTime() : 0;

  return {
    ...patient,
    status: normalizeBillingStatus(patient.status),
    patientName,
    dob,
    dobSort: dob ? new Date(dob).getTime() : 0,
    model: patient.model || 'N/A',
    serial: patient.serial || 'N/A',
    rpmStartDate,
    rpmStartDateSort: Number.isNaN(rpmMs) ? 0 : rpmMs,
    uploadDate: uploadRaw || '',
    uploadDateSort: Number.isNaN(uploadMs) ? 0 : uploadMs,
    measurementDays: Number(patient.measurement_days ?? 0),
    serviceTime: Number(patient.service_time ?? 0),
    eligibleCptCodes: patient.eligible_cpt_codes || 'N/A',
    vitals: patient.vitals || '-',
    caregiverDisplay: patient.caregiver_name || 'Caregiver N/A',
    providerDisplay: patient.provider_name || formatPersonName(patient.provider_last_name, patient.provider_first_name) || 'Provider N/A'
  };
};

const getBillingSortValue = (row, key) => {
  switch (key) {
    case 'status':
      return getStatusOrder(row.status);
    case 'patientName':
      return String(row.patientName || '').toLowerCase();
    case 'dob':
      return row.dobSort ?? 0;
    case 'rpmStartDate':
      return row.rpmStartDateSort ?? 0;
    case 'uploadDate':
      return row.uploadDateSort ?? 0;
    case 'measurementDays':
      return row.measurementDays ?? 0;
    case 'serviceTime':
      return row.serviceTime ?? 0;
    case 'caregiverDisplay':
      return String(row.caregiverDisplay || '').toLowerCase();
    case 'providerDisplay':
      return String(row.providerDisplay || '').toLowerCase();
    default:
      return String(row[key] ?? '').toLowerCase();
  }
};

const sortBillingPatients = (list, sortBy, sortOrder) => {
  const dir = sortOrder === 'asc' ? 1 : -1;
  return [...list].sort((a, b) => {
    const statusA = getStatusOrder(a.status);
    const statusB = getStatusOrder(b.status);
    if (statusA !== statusB) return statusA - statusB;

    const valueA = getBillingSortValue(a, sortBy);
    const valueB = getBillingSortValue(b, sortBy);

    if (typeof valueA === 'number' && typeof valueB === 'number') {
      if (valueA !== valueB) return dir * (valueA - valueB);
    } else {
      const cmp = String(valueA).localeCompare(String(valueB), undefined, {
        numeric: true,
        sensitivity: 'base'
      });
      if (cmp !== 0) return dir * cmp;
    }

    return String(a.patientName || '').localeCompare(String(b.patientName || ''), undefined, {
      sensitivity: 'base'
    });
  });
};

const ManageBillingReport = () => {
  const { practiceId } = useParams();
  const location = useLocation();
  const { updateBreadcrumbs } = useBreadcrumb();

  const includeOptionsNextFetchRef = useRef(true);
  const filtersRef = useRef(null);

  const [practice, setPractice] = useState(null);
  const [tableLoading, setTableLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [listReady, setListReady] = useState(false);
  const [patients, setPatients] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 30,
    total: 0,
    total_pages: 1
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [entriesPerPage, setEntriesPerPage] = useState('30');
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('patientName');
  const [sortOrder, setSortOrder] = useState('asc');

  const rpmStartInputRef = useRef(null);
  const rpmEndInputRef = useRef(null);

  const openDatePicker = (ref) => {
    const el = ref?.current;
    if (!el) return;
    try {
      el.showPicker?.();
    } catch {
      el.focus();
    }
  };

  // Filter states
  const [filters, setFilters] = useState({
    cptCode: '',
    model: '',
    status: '',
    patientName: '',
    serial: '',
    selectData: '',
    insurance: '',
    serviceTime: '',
    measurementDays: '',
    caregiver: '',
    provider: '',
    rpmStartDate: '',
    rpmEndDate: ''
  });

  // Dropdown options (these would come from API in real implementation)
  const [dropdownOptions, setDropdownOptions] = useState({
    cptCodes: [],
    models: [],
    statuses: ['Active', 'Pending', 'Lost'],
    patients: [],
    measurementDays: ['0', '1-10', '11-20', '21-30', '31+'],
    caregivers: [],
    providers: []
  });

  const toInputDate = (value) => {
    if (!value) return '';
    const iso = String(value).trim().match(/^(\d{4}-\d{2}-\d{2})/);
    if (iso) return iso[1];
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const buildDefaultFiltersFromPractice = (practiceData) => {
    const { start, end } = getPracticeBillingPeriod(practiceData);
    const rpmStart = toInputDate(start);
    const rpmEnd = toInputDate(end);
    return {
      cptCode: '',
      model: '',
      status: '',
      patientName: '',
      serial: '',
      selectData: '',
      insurance: '',
      serviceTime: '',
      measurementDays: '',
      caregiver: '',
      provider: '',
      rpmStartDate: rpmStart,
      rpmEndDate: rpmEnd
    };
  };

  const applyPracticeContext = useCallback((practiceData) => {
    if (!practiceData) return;
    setPractice(practiceData);
    updateBreadcrumbs([
      { label: 'Billing & Reports', path: '/billing' },
      { label: practiceData.practice_name || 'Billing Report', path: null, active: true }
    ]);
    setFilters(buildDefaultFiltersFromPractice(practiceData));
    writeBillingPracticeCache(practiceData);
  }, [updateBreadcrumbs]);

  const applyDropdownOptions = (options) => {
    if (!options) return;
    setDropdownOptions({
      cptCodes: options.cptCodes || [],
      models: options.models || [],
      statuses: ['Active', 'Pending', 'Lost'],
      patients: options.patients || [],
      measurementDays: ['0', '1-10', '11-20', '21-30', '31+'],
      caregivers: options.caregivers || [],
      providers: options.providers || []
    });
  };

  filtersRef.current = filters;

  useEffect(() => {
    if (!practiceId) return;

    const prefetched =
      (location.state?.practice && String(location.state.practice.id) === String(practiceId)
        ? location.state.practice
        : null) ||
      readBillingPracticeCache(practiceId);

    if (prefetched) {
      applyPracticeContext(prefetched);
    } else {
      updateBreadcrumbs([
        { label: 'Billing & Reports', path: '/billing' },
        { label: 'Billing Report', path: null, active: true }
      ]);
    }

    includeOptionsNextFetchRef.current = true;
    setCurrentPage(1);
    setSearchTerm('');
    setDebouncedSearch('');
    setHasLoadedOnce(false);
    setListReady(true);
  }, [practiceId, location.state, applyPracticeContext, updateBreadcrumbs]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (!practiceId || !listReady) return;
    fetchBillingData();
  }, [practiceId, listReady, currentPage, entriesPerPage, debouncedSearch]);

  // Close export menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showExportMenu && !event.target.closest('.export-dropdown')) {
        setShowExportMenu(false);
      }
    };

    if (showExportMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showExportMenu]);

  const fetchBillingData = async (options = {}) => {
    if (!practiceId) return;

    const activeFilters = options.filtersOverride || filtersRef.current || filters;
    const page = options.page ?? currentPage;
    const limit =
      options.limit ??
      (entriesPerPage === 'all' ? 1000 : Number.parseInt(entriesPerPage, 10) || 30);
    const includeOptions =
      options.includeOptions ?? includeOptionsNextFetchRef.current;

    setTableLoading(true);
    try {
      const params = { page, limit };

      if (includeOptions) {
        params.includeOptions = '1';
      }

      if (debouncedSearch.trim()) {
        params.search = debouncedSearch.trim();
      }

      Object.keys(activeFilters).forEach((key) => {
        const value = activeFilters[key];
        if (value !== null && value !== undefined && value.toString().trim() !== '') {
          params[key] = value;
        }
      });

      const response = await billingService.getBillingReports(practiceId, params);
      const rows = response?.data?.patients ?? response?.patients;
      if (response?.success) {
        if (response.data?.practice) {
          applyPracticeContext(response.data.practice);
        }
        if (response.data?.options) {
          applyDropdownOptions(response.data.options);
          includeOptionsNextFetchRef.current = false;
        }
        setPatients(Array.isArray(rows) ? rows : []);
        if (response?.data?.pagination) {
          setPagination(response.data.pagination);
        }
      } else {
        toast.error(response?.message || 'Failed to load billing data');
      }
    } catch (error) {
      console.error('Error fetching billing data:', error);
      toast.error('Failed to load billing data');
    } finally {
      setTableLoading(false);
      setHasLoadedOnce(true);
    }
  };

  const fetchAllBillingPatients = async (filtersOverride = null) => {
    if (!practiceId) return [];

    const activeFilters = filtersOverride || filters;
    const params = { exportAll: '1' };

    if (debouncedSearch.trim()) {
      params.search = debouncedSearch.trim();
    }

    Object.keys(activeFilters).forEach((key) => {
      const value = activeFilters[key];
      if (value !== null && value !== undefined && value.toString().trim() !== '') {
        params[key] = value;
      }
    });

    const response = await billingService.getBillingReports(practiceId, params);
    const rows = response?.data?.patients ?? response?.patients;
    return Array.isArray(rows) ? rows.map(mapBillingPatientRow) : [];
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleQuery = () => {
    setCurrentPage(1);
    fetchBillingData({ page: 1, filtersOverride: filters, includeOptions: false });
  };

  const handleReset = async () => {
    const defaultFilters = buildDefaultFiltersFromPractice(practice);
    setFilters(defaultFilters);
    setSearchTerm('');
    setDebouncedSearch('');
    setCurrentPage(1);
    await fetchBillingData({ page: 1, filtersOverride: defaultFilters });
  };

  const handlePrint = async () => {
    setTableLoading(true);
    try {
      const allFilteredPatients = await fetchAllBillingPatients();

      if (allFilteredPatients.length === 0) {
        toast.warning('No data to print');
        return;
      }

      // Hide filter form and toolbar for printing
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        toast.error('Please allow popups to print');
        return;
      }

      const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Billing Report - ${practice?.practice_name || 'Report'}</title>
          <style>
            @media print {
              @page {
                margin: 0.5in;
                size: letter landscape;
              }
              body {
                font-family: Arial, sans-serif;
                font-size: 10px;
                margin: 0;
                padding: 0;
              }
              .print-header {
                text-align: center;
                margin-bottom: 20px;
                border-bottom: 2px solid #000;
                padding-bottom: 10px;
              }
              .print-header h2 {
                margin: 0 0 5px 0;
                font-size: 18px;
              }
              .print-header .billing-period {
                font-size: 12px;
                color: #666;
              }
              .print-date {
                text-align: right;
                font-size: 10px;
                margin-bottom: 10px;
              }
              table {
                width: 100%;
                border-collapse: collapse;
                font-size: 9px;
              }
              th, td {
                border: 1px solid #000;
                padding: 4px;
                text-align: left;
              }
              th {
                background-color: #f0f0f0;
                font-weight: bold;
              }
              tr:nth-child(even) {
                background-color: #f9f9f9;
              }
              .no-print {
                display: none;
              }
            }
          </style>
        </head>
        <body>
          <div class="print-header">
            <h2>${practice?.practice_name || 'Billing Report'}</h2>
            <div class="billing-period">Billing Period: ${getBillingPeriod()}</div>
          </div>
          <div class="print-date">Generated: ${new Date().toLocaleString()}</div>
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Status</th>
                <th>Vitals</th>
                <th>Patient Name</th>
                <th>DOB</th>
                <th>Modal #</th>
                <th>Serial #</th>
                <th>RPM Start Date</th>
                <th>Upload Date/Time</th>
                <th>Measurement Days</th>
                <th>Service Time</th>
                <th>Caregiver</th>
                <th>Provider</th>
                <th>Eligible Cpt Codes</th>
              </tr>
            </thead>
            <tbody>
              ${allFilteredPatients.map((patient, idx) => `
                <tr>
                  <td>${idx + 1}</td>
                  <td>${patient.status || 'N/A'}</td>
                  <td>${patient.vitals || 'N/A'}</td>
                  <td><strong>${patient.patientName || 'N/A'}</strong></td>
                  <td>${patient.dob ? formatDate(patient.dob) : 'N/A'}</td>
                  <td>${patient.model || 'N/A'}</td>
                  <td>${patient.serial || 'N/A'}</td>
                  <td>${patient.rpmStartDate ? formatDate(patient.rpmStartDate) : 'N/A'}</td>
                  <td>${patient.uploadDate ? formatDateTime(patient.uploadDate) : ''}</td>
                  <td>${patient.measurementDays ?? 0}</td>
                  <td>${patient.serviceTime ?? 0}</td>
                  <td>${patient.caregiverDisplay || 'N/A'}</td>
                  <td>${patient.providerDisplay || 'N/A'}</td>
                  <td>${patient.eligibleCptCodes || 'N/A'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          <div style="margin-top: 20px; font-size: 10px; text-align: center;">
            Total Records: ${allFilteredPatients.length}
          </div>
        </body>
      </html>
    `;

      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();

      // Wait for content to load, then print
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 250);
    } catch (error) {
      console.error('Error printing billing report:', error);
      toast.error('Failed to print billing report');
    } finally {
      setTableLoading(false);
    }
  };

  const handleExport = async (format = 'csv') => {
    setTableLoading(true);
    try {
      const allFilteredPatients = await fetchAllBillingPatients();

      if (allFilteredPatients.length === 0) {
        toast.warning('No data to export');
        return;
      }

      const headers = ['#', 'Status', 'Vitals', 'Patient Name', 'DOB', 'Modal #', 'Serial #', 'RPM Start Date', 'Upload Date/Time', 'Measurement Days', 'Service Time', 'Caregiver', 'Provider', 'Eligible Cpt Codes'];
      const rows = allFilteredPatients.map((patient, idx) => [
        idx + 1,
        patient.status || 'N/A',
        patient.vitals || 'N/A',
        patient.patientName || 'N/A',
        patient.dob ? formatDate(patient.dob) : 'N/A',
        patient.model || 'N/A',
        patient.serial || 'N/A',
        patient.rpmStartDate ? formatDate(patient.rpmStartDate) : 'N/A',
        patient.uploadDate ? formatDateTime(patient.uploadDate) : 'N/A',
        patient.measurementDays ?? 0,
        patient.serviceTime ?? 0,
        patient.caregiverDisplay || 'N/A',
        patient.providerDisplay || 'N/A',
        patient.eligibleCptCodes || 'N/A'
      ]);

      if (format === 'csv') {
        const csvContent = [
          headers.join(','),
          ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `billing-report-${practice?.practice_name || 'report'}-${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success('Report exported to CSV');
      } else if (format === 'pdf') {
        try {
          // Create a temporary element for PDF generation
          const element = document.createElement('div');
          element.style.padding = '20px';
          element.style.fontFamily = 'Arial, sans-serif';

          element.innerHTML = `
          <div style="text-align: center; margin-bottom: 20px; border-bottom: 2px solid #000; padding-bottom: 10px;">
            <h2 style="margin: 0 0 5px 0; font-size: 18px;">${practice?.practice_name || 'Billing Report'}</h2>
            <div style="font-size: 12px; color: #666;">Billing Period: ${getBillingPeriod()}</div>
          </div>
          <div style="text-align: right; font-size: 10px; margin-bottom: 10px;">
            Generated: ${new Date().toLocaleString()}
          </div>
          <table style="width: 100%; border-collapse: collapse; font-size: 9px;">
            <thead>
              <tr style="background-color: #f0f0f0;">
                ${headers.map(h => `<th style="border: 1px solid #000; padding: 6px; text-align: left; font-weight: bold;">${h}</th>`).join('')}
              </tr>
            </thead>
            <tbody>
              ${rows.map((row, idx) => `
                <tr style="${idx % 2 === 0 ? 'background-color: #f9f9f9;' : ''}">
                  ${row.map(cell => `<td style="border: 1px solid #000; padding: 4px;">${String(cell)}</td>`).join('')}
                </tr>
              `).join('')}
            </tbody>
          </table>
          <div style="margin-top: 20px; font-size: 10px; text-align: center;">
            Total Records: ${allFilteredPatients.length}
          </div>
        `;

          document.body.appendChild(element);

          const opt = {
            margin: [0.5, 0.5, 0.5, 0.5],
            filename: `billing-report-${practice?.practice_name || 'report'}-${new Date().toISOString().split('T')[0]}.pdf`,
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: { scale: 2, useCORS: true },
            jsPDF: { unit: 'in', format: 'letter', orientation: 'landscape' }
          };

          await html2pdf().set(opt).from(element).save();
          document.body.removeChild(element);
          toast.success('Report exported to PDF');
        } catch (error) {
          console.error('Error exporting PDF:', error);
          toast.error('Failed to export PDF');
        }
      }
    } catch (error) {
      console.error('Error exporting billing report:', error);
      toast.error('Failed to export billing report');
    } finally {
      setTableLoading(false);
    }
  };

  const handleEmail = () => {
    toast.info('Email feature coming soon');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleSort = (key) => {
    if (sortBy === key) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };

  const mappedPatients = useMemo(
    () => (Array.isArray(patients) ? patients.map(mapBillingPatientRow) : []),
    [patients]
  );

  const filteredPatients = useMemo(
    () => sortBillingPatients(mappedPatients, sortBy, sortOrder),
    [mappedPatients, sortBy, sortOrder]
  );

  const pageSize = useMemo(() => {
    if (entriesPerPage === 'all') return Math.max(pagination.per_page || mappedPatients.length, 1);
    const n = Number.parseInt(entriesPerPage, 10);
    return Number.isFinite(n) && n > 0 ? n : 30;
  }, [entriesPerPage, pagination.per_page, mappedPatients.length]);

  const totalPages = Math.max(1, pagination.total_pages || 1);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const billingTableColumns = useMemo(() => [
    {
      key: 'rowNo',
      title: '#',
      className: 'reusable-table-centered-col',
      render: (_row, rowIndex) => (currentPage - 1) * pageSize + rowIndex + 1
    },
    {
      key: 'status',
      title: 'Status',
      sortable: true,
      className: 'reusable-table-centered-col',
      render: (row) => <StatusBadge status={row.status || 'N/A'} />
    },
    { key: 'vitals', title: 'Vitals', sortable: true, render: (row) => row.vitals || '-' },
    {
      key: 'patientName',
      title: 'Patient Name',
      sortable: true,
      render: (row) => <strong>{row.patientName}</strong>
    },
    {
      key: 'dob',
      title: 'DOB',
      sortable: true,
      render: (row) => (row.dob ? formatDate(row.dob) : 'N/A')
    },
    { key: 'model', title: 'Modal #', sortable: true, render: (row) => row.model || 'N/A' },
    { key: 'serial', title: 'Serial #', sortable: true, render: (row) => row.serial || 'N/A' },
    {
      key: 'rpmStartDate',
      title: 'RPM Start Date',
      sortable: true,
      render: (row) => (row.rpmStartDate ? formatDate(row.rpmStartDate) : 'N/A')
    },
    {
      key: 'uploadDate',
      title: 'Upload Date/Time',
      sortable: true,
      render: (row) => (row.uploadDate ? formatDateTime(row.uploadDate) : '')
    },
    {
      key: 'measurementDays',
      title: 'Measurement Days',
      sortable: true,
      className: 'reusable-table-centered-col',
      render: (row) => String(row.measurementDays ?? 0)
    },
    {
      key: 'serviceTime',
      title: 'Service Time',
      sortable: true,
      className: 'reusable-table-centered-col',
      render: (row) => String(row.serviceTime ?? 0)
    },
    {
      key: 'caregiverDisplay',
      title: 'Caregiver',
      sortable: true,
      render: (row) => row.caregiverDisplay || 'Caregiver N/A'
    },
    {
      key: 'providerDisplay',
      title: 'Provider',
      sortable: true,
      render: (row) => row.providerDisplay || 'Provider N/A'
    },
    {
      key: 'eligibleCptCodes',
      title: 'Eligible Cpt Codes',
      sortable: true,
      render: (row) => row.eligibleCptCodes || 'N/A'
    }
  ], [currentPage, pageSize]);

  const getBillingPeriod = () => {
    const { start, end } = getPracticeBillingPeriod(practice);
    if (start && end) {
      return `${formatDate(start)} - ${formatDate(end)}`;
    }
    return 'N/A';
  };

  return (
    <div className="manage-billing-report-wrapper">
      <header className="manage-billing-header">
        <Heading title={practice?.practice_name || 'Billing Report'} level="h2" className="manage-billing-title" />
        <div className="manage-billing-period">
          Billing Period: <span>{getBillingPeriod()}</span>
        </div>
      </header>

      <div className="manage-billing-container">
        {/* Filter Form Card */}
        <div className="manage-billing-form-card">
          <div className="card-head">All Report</div>
          <form
            className="manage-billing-form"
            onSubmit={(e) => {
              e.preventDefault();
              handleQuery();
            }}
          >
            <div className="filter-grid">

              {/* Row 1 */}
              <div className="form-field">
                <label>CPT Code</label>
                <select
                  className="form-select-custom"
                  value={filters.cptCode}
                  onChange={(e) => handleFilterChange('cptCode', e.target.value)}
                >
                  <option value="">-- Select --</option>
                  {dropdownOptions.cptCodes.map(code => (
                    <option key={code} value={code}>{code}</option>
                  ))}
                </select>
              </div>

              <div className="form-field">
                <label>Patient Name</label>
                <select
                  className="form-select-custom"
                  value={filters.patientName}
                  onChange={(e) => handleFilterChange('patientName', e.target.value)}
                >
                  <option value="">-- Select --</option>
                  {dropdownOptions.patients.map(patient => (
                    <option key={patient.id} value={patient.id}>
                      {patient.first_name} {patient.last_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-field">
                <label>Insurance</label>
                <input
                  type="text"
                  className="form-input-custom"
                  value={filters.insurance}
                  onChange={(e) => handleFilterChange('insurance', e.target.value)}
                  placeholder="Enter insurance"
                />
              </div>

              <div className="form-field">
                <label>Service Time</label>
                <input
                  type="text"
                  className="form-input-custom"
                  value={filters.serviceTime}
                  onChange={(e) => handleFilterChange('serviceTime', e.target.value)}
                  placeholder="Enter service time"
                />
              </div>

              <div className="form-field">
                <label>RPM Service Date Range</label>
                <div className="date-range-inputs mbr-rpm-date-range">
                  <div className="mbr-date-field">
                    <input
                      ref={rpmStartInputRef}
                      type="date"
                      className="form-input-custom mbr-date-input"
                      value={filters.rpmStartDate}
                      onChange={(e) => handleFilterChange('rpmStartDate', e.target.value)}
                      onClick={(e) => {
                        try {
                          e.currentTarget.showPicker?.();
                        } catch {
                          /* ignore */
                        }
                      }}
                    />
                    <button
                      type="button"
                      className="mbr-date-calendar-btn"
                      aria-label="Open start date calendar"
                      onClick={() => openDatePicker(rpmStartInputRef)}
                    >
                      <FontAwesomeIcon icon={faCalendarAlt} />
                    </button>
                  </div>
                  <span className="date-sep">-</span>
                  <div className="mbr-date-field">
                    <input
                      ref={rpmEndInputRef}
                      type="date"
                      className="form-input-custom mbr-date-input"
                      value={filters.rpmEndDate}
                      onChange={(e) => handleFilterChange('rpmEndDate', e.target.value)}
                      onClick={(e) => {
                        try {
                          e.currentTarget.showPicker?.();
                        } catch {
                          /* ignore */
                        }
                      }}
                    />
                    <button
                      type="button"
                      className="mbr-date-calendar-btn"
                      aria-label="Open end date calendar"
                      onClick={() => openDatePicker(rpmEndInputRef)}
                    >
                      <FontAwesomeIcon icon={faCalendarAlt} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Row 2 */}
              <div className="form-field">
                <label>Model #</label>
                <select
                  className="form-select-custom"
                  value={filters.model}
                  onChange={(e) => handleFilterChange('model', e.target.value)}
                >
                  <option value="">-- Select --</option>
                  {dropdownOptions.models.map(model => (
                    <option key={model} value={model}>{model}</option>
                  ))}
                </select>
              </div>

              <div className="form-field">
                <label>Serial #</label>
                <input
                  type="text"
                  className="form-input-custom"
                  value={filters.serial}
                  onChange={(e) => handleFilterChange('serial', e.target.value)}
                  placeholder="Enter serial number"
                />
              </div>

              <div className="form-field">
                <label>Measurement Days</label>
                <select
                  className="form-select-custom"
                  value={filters.measurementDays}
                  onChange={(e) => handleFilterChange('measurementDays', e.target.value)}
                >
                  <option value="">-- Select --</option>
                  {dropdownOptions.measurementDays.map(days => (
                    <option key={days} value={days}>{days}</option>
                  ))}
                </select>
              </div>

              {/* No placeholders here - Status will flow to next column in Row 2 */}
              <div className="form-field">
                <label>Status</label>
                <select
                  className="form-select-custom"
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  <option value="">-- Select --</option>
                  {dropdownOptions.statuses.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>

              <div className="form-field">
                <label>Select Data for the Report</label>
                <input
                  type="text"
                  className="form-input-custom"
                  value={filters.selectData}
                  onChange={(e) => handleFilterChange('selectData', e.target.value)}
                  placeholder="Enter data"
                />
              </div>

              {/* Row 3 */}
              <div className="form-field">
                <label>Caregiver</label>
                <select
                  className="form-select-custom"
                  value={filters.caregiver}
                  onChange={(e) => handleFilterChange('caregiver', e.target.value)}
                >
                  <option value="">-- Select --</option>
                  {dropdownOptions.caregivers.map(caregiver => (
                    <option key={caregiver.id} value={caregiver.id}>
                      {caregiver.first_name} {caregiver.last_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-field">
                <label>Provider</label>
                <select
                  className="form-select-custom"
                  value={filters.provider}
                  onChange={(e) => handleFilterChange('provider', e.target.value)}
                >
                  <option value="">-- Select --</option>
                  {dropdownOptions.providers.map(provider => (
                    <option key={provider.id} value={provider.id}>
                      {provider.first_name} {provider.last_name}
                    </option>
                  ))}
                </select>
              </div>

            </div>

            {/* Action Buttons */}
            <div className="form-actions">
              <div className="spacer" />
              <div className="actions-right">
                <button type="button" className="reset-btn" onClick={handleReset}>
                  <FontAwesomeIcon icon={faUndo} /> Reset
                </button>
                <button type="submit" className="query-btn">
                  <FontAwesomeIcon icon={faSearch} /> Query
                </button>
                <button type="button" className="print-btn" onClick={handlePrint}>
                  <FontAwesomeIcon icon={faPrint} /> Print
                </button>
                <div className="export-dropdown">
                  <button
                    type="button"
                    className="export-btn"
                    onClick={() => setShowExportMenu(!showExportMenu)}
                  >
                    <FontAwesomeIcon icon={faFileExport} /> Export <FontAwesomeIcon icon={faChevronDown} style={{ fontSize: '10px', marginLeft: '2px' }} />
                  </button>
                  <div className={`export-menu ${showExportMenu ? 'show' : ''}`}>
                    <button
                      type="button"
                      onClick={() => {
                        handleExport('csv');
                        setShowExportMenu(false);
                      }}
                    >
                      Export to CSV
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        handleExport('pdf');
                        setShowExportMenu(false);
                      }}
                    >
                      Export to PDF
                    </button>
                  </div>
                </div>
                <button type="button" className="email-btn" onClick={handleEmail}>
                  <FontAwesomeIcon icon={faEnvelope} /> Email
                </button>
              </div>
            </div>
          </form>
        </div>

        {/* Data Table */}
        <div className="manage-billing-table-section">
          {/* Top Toolbar: Search Only (Top Right) */}
          <div className="billing-table-toolbar">
            <div className="spacer"></div>

            {/* Search Bar Top Right */}
            <SearchBar value={searchTerm} onChange={setSearchTerm} className="search-block" />
          </div>

          <div className="table-wrap">
            <Table
              columns={billingTableColumns}
              data={filteredPatients}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSort={handleSort}
              isLoading={tableLoading}
              loadingMode="skeleton"
              skeletonRows={Math.min(pageSize, 10)}
              emptyMessage={
                tableLoading || !hasLoadedOnce
                  ? ''
                  : 'No billing data found'
              }
            />
          </div>

          {(pagination.total > 0 || filteredPatients.length > 0) && (
            <ListScreenPaginationBar
              entriesValue={entriesPerPage === 'all' ? 'all' : String(entriesPerPage)}
              onEntriesChange={(value) => {
                setEntriesPerPage(value === 'all' ? 'all' : value);
                setCurrentPage(1);
              }}
              showPageSelector={entriesPerPage !== 'all'}
              currentPage={pagination.current_page || currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          )}

        </div>
      </div>
    </div>
  );
};

export default ManageBillingReport;
